// ============================================
// Poker Massage App - TypeScript Types
// ============================================

import { Timestamp } from 'firebase/firestore';

// ============================================
// Enums and Constants
// ============================================

export type UserRole = 'superadmin' | 'admin' | 'therapist';

export type EventStatus = 'draft' | 'live' | 'closed';

export type AssignmentMode = 'auto_assign' | 'open_queue';

export type DutyStatus = 'on_duty' | 'break' | 'off_duty';

export type TherapistUIStatus = 'green' | 'orange' | 'red' | 'blue' | 'grey';

export type BookingStatus =
  | 'pending'
  | 'assigned'
  | 'accepted'
  | 'cancelled_by_player'
  | 'cancelled_by_therapist'
  | 'declined'
  | 'expired'
  | 'started'
  | 'completed'
  | 'no_show';

export type SessionStatus = 'active' | 'completed' | 'voided';

export type TherapistChoice = 'specific' | 'random';

export type DevicePlatform = 'web' | 'ios' | 'android';

// ============================================
// User Models
// ============================================

export interface User {
  uid: string;
  displayName: string;
  email: string;
  photoUrl?: string;
  createdAt: Timestamp;
  lastLoginAt: Timestamp;
}

export interface UserDevice {
  deviceId: string;
  fcmToken: string;
  platform: DevicePlatform;
  createdAt: Timestamp;
  lastSeenAt: Timestamp;
}

export interface Player {
  playerUid: string;
  createdAt: Timestamp;
  lastSeenAt: Timestamp;
}

export interface PlayerDevice {
  deviceId: string;
  fcmToken: string;
  platform: DevicePlatform;
  createdAt: Timestamp;
  lastSeenAt: Timestamp;
}

// ============================================
// Event Models
// ============================================

export interface Event {
  id: string;
  name: string;
  venueName: string;
  timezone: string;
  status: EventStatus;

  // Date and Location
  startDate?: string; // ISO date string YYYY-MM-DD
  endDate?: string;   // ISO date string YYYY-MM-DD
  city?: string;
  country?: string;

  // Layout
  tablesCount: number;
  seatsPerTable: number;

  // Assignment
  assignmentMode: AssignmentMode;

  // Timing thresholds
  acceptWindowMin: number; // default 5
  orangeThresholdMin: number; // default 15
  offlineThresholdSec: number; // fixed 90

  // Player location changes
  allowPlayerLocationChanges: boolean;
  locationChangeMaxCount: number; // default 3
  locationChangeCooldownSec: number; // default 30

  // Pricing
  currency: string; // default EUR
  pricePerMinute: number; // default 2

  // Revenue splits
  therapistBillPct: number; // default 0.5
  eventBillPct: number; // default 0.5
  therapistTipPct: number; // default 1.0

  // Underpayment
  allowUnderpayment: boolean;
  requireUnderpaymentReason: boolean;

  // Booking/extend options
  bookingMinuteOptions: number[]; // default [10, 15, 20, 30]
  extendMinuteOptions: number[]; // default [5, 10, 15]
  defaultBookingMinutes: number; // default 30 - used in player booking

  // Customer feedback options
  askCustomerAmount: boolean;  // Ask customer how much they paid
  askCustomerRating: boolean;  // Ask customer to rate 1-5 stars

  // Metadata
  createdAt: Timestamp;
  createdBy: string;
}

export interface EventInvite {
  id: string;
  code: string;
  role: 'therapist' | 'admin';
  maxUses: number;
  uses: number;
  expiresAt?: Timestamp;
  createdAt: Timestamp;
  createdBy: string;
}

export interface EventAdmin {
  uid: string;
  email: string;
  displayName: string;
  addedAt: Timestamp;
  addedBy: string;
}

export type PhotoStatus = 'pending' | 'approved' | 'rejected';

export interface EventTherapist {
  uid: string;
  displayName: string;           // Real name (for admin/internal use)
  publicDisplayName?: string;    // Name shown to players (optional, defaults to displayName)
  photoUrl?: string;
  thumbnailUrl?: string;
  joinedAt: Timestamp;

  // Photo approval workflow
  originalPhotoUrl?: string;      // Full uncropped image uploaded by therapist
  photoStatus?: PhotoStatus;      // Approval status
  photoUploadedAt?: Timestamp;    // When photo was uploaded
  photoApprovedAt?: Timestamp;    // When admin approved
  photoApprovedBy?: string;       // Admin UID who approved

  // Duty and status
  dutyStatus: DutyStatus;
  uiStatus: TherapistUIStatus;

  // Shift tracking
  checkedInAt?: Timestamp;
  checkedOutAt?: Timestamp;

  // Break tracking (current break)
  breakStartedAt?: Timestamp;

  // Presence
  lastHeartbeatAt: Timestamp;

  // Current work
  currentAcceptedRequestId?: string;
  activeSessionId?: string;
  activeSessionEndsAt?: Timestamp;

  // For auto-assign algorithm
  lastSessionCompletedAt?: Timestamp;

  // Personal settings (overrides event defaults)
  defaultBookingMinutes?: number; // Override event default for this therapist
}

// ============================================
// Booking Models
// ============================================

export interface LocationHistoryEntry {
  tableNo: number;
  seatNo: number;
  updatedAt: Timestamp;
}

export interface BookingRequest {
  id: string;
  createdAt: Timestamp;
  playerUid: string;

  // Location
  tableNo: number;
  seatNo: number;

  // Request details
  requestedMinutes: number;
  therapistChoice: TherapistChoice;
  preferredTherapistId?: string;

  // Snapshot of event setting
  assignmentModeSnapshot: AssignmentMode;

  // Status
  status: BookingStatus;

  // Assignment
  assignedTherapistId?: string;
  acceptedAt?: Timestamp;
  etaMinutes?: number;
  etaUpdatedAt?: Timestamp;

  // Session tracking
  startedAt?: Timestamp;
  completedAt?: Timestamp;

  // Cancellation
  cancelReason?: string;

  // Location updates
  locationUpdatedAt?: Timestamp;
  locationUpdateCount: number;
  locationHistory: LocationHistoryEntry[];
  therapistAckLocationAt?: Timestamp;
}

// ============================================
// Session Models
// ============================================

export interface SessionExtension {
  minutes: number;
  addedAt: Timestamp;
}

export interface Session {
  id: string;
  requestId: string;
  therapistId: string;
  playerUid: string;

  // Location
  tableNo: number;
  seatNo: number;

  // Timing
  initialMinutes: number;
  startedAt: Timestamp;
  endsAt: Timestamp;
  extensions: SessionExtension[];
  totalMinutes: number;
  actualMinutes?: number;      // Actual duration when completed
  earlyEndMinutes?: number;    // Minutes early if session ended before scheduled time

  // Status
  status: SessionStatus;
  completedAt?: Timestamp;

  // Payment snapshots
  currencySnapshot: string;
  pricePerMinuteSnapshot: number;
  billAmount: number;

  // Payment logging
  amountReceived?: number;
  tipAmount?: number;
  underpaidAmount?: number;

  // Split snapshots
  therapistBillPctSnapshot: number;
  eventBillPctSnapshot: number;
  therapistTipPctSnapshot: number;

  // Payout calculations
  therapistBillPayout?: number;
  therapistTipPayout?: number;
  therapistTotalPayout?: number;
  eventPayout?: number;

  // Payment metadata
  paymentEnteredAt?: Timestamp;
  paymentEnteredBy?: string;
  amountEditableUntil?: Timestamp;
  underpaymentReason?: string;
  lastAmountEditAt?: Timestamp;
  lastAmountEditBy?: string;

  // Customer feedback (optional, based on event settings)
  customerPaidAmount?: number;      // Amount customer says they paid
  customerRating?: number;          // 1-5 star rating
  customerFeedbackAt?: Timestamp;   // When feedback was submitted
  paymentDiscrepancy?: number;      // Difference between amountReceived and customerPaidAmount

  // Pause/resume functionality (for tournament breaks)
  isPaused?: boolean;               // Whether session is currently paused
  pausedAt?: Timestamp;             // When current pause started
  totalPausedSeconds?: number;      // Cumulative pause time in seconds
}

// ============================================
// Notification Models
// ============================================

export interface Notification {
  id: string;
  toUid?: string;
  toPlayerUid?: string;
  type: string;
  title: string;
  body: string;
  data: Record<string, unknown>;
  createdAt: Timestamp;
  deliveredAt?: Timestamp;
  readAt?: Timestamp;
}

// ============================================
// Audit Models
// ============================================

export interface AuditEntry {
  id: string;
  actorUid: string;
  action: string;
  entityType: string;
  entityId: string;
  ts: Timestamp;
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
  reason?: string;
}

// ============================================
// Stats Models
// ============================================

export interface DailyEventStats {
  date: string; // yyyyMMdd
  requestsCreated: number;
  requestsAccepted: number;
  requestsExpired: number;
  requestsCancelledByPlayer: number;
  requestsCancelledByTherapist: number;
  requestsNoShow: number;
  sessionsCompleted: number;
  minutesDelivered: number;
  billAmountTotal: number;
  amountReceivedTotal: number;
  tipsTotal: number;
  underpaidTotal: number;
  therapistPayoutTotal: number;
  eventPayoutTotal: number;
  avgTimeToAcceptSec: number;
  avgTimeAcceptToStartSec: number;
  peakConcurrentSessions: number;
  peakHour?: number;
}

export interface DailyTherapistStats {
  date: string;
  therapistId: string;
  checkedInAt?: Timestamp;
  checkedOutAt?: Timestamp;
  workSeconds: number;
  sessionSeconds: number;
  utilizationPct: number;
  requestsAccepted: number;
  sessionsCompleted: number;
  minutesDelivered: number;
  billAmountTotal: number;
  amountReceivedTotal: number;
  tipsTotal: number;
  underpaidTotal: number;
  therapistPayoutTotal: number;
}

// ============================================
// Attendance Models
// ============================================

/**
 * Daily attendance record for a therapist
 * Stored at: /events/{eventId}/therapists/{uid}/attendance/{date}
 * where date is YYYY-MM-DD format
 */
export interface TherapistAttendance {
  date: string; // YYYY-MM-DD
  therapistId: string;

  // Check-in/out times for the day
  checkedInAt: Timestamp;
  checkedOutAt?: Timestamp;

  // Calculated fields (updated on check-out)
  venueMinutes?: number;  // Total minutes at venue

  // Break tracking
  breakSegments?: BreakSegment[];
  totalBreakMinutes?: number;  // Sum of all break durations

  // Multiple check-ins per day support (optional for future)
  // If therapist leaves and comes back, we track all segments
  segments?: AttendanceSegment[];
}

export interface AttendanceSegment {
  checkedInAt: Timestamp;
  checkedOutAt?: Timestamp;
  minutes?: number;
}

export interface BreakSegment {
  startedAt: Timestamp;
  endedAt?: Timestamp;
  minutes?: number;
}

// ============================================
// Helper Types
// ============================================

export interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId?: string;
}

// Custom claims type for auth
export interface CustomClaims {
  role?: UserRole;
}

// Event defaults
export const EVENT_DEFAULTS = {
  acceptWindowMin: 5,
  orangeThresholdMin: 15,
  offlineThresholdSec: 90,
  allowPlayerLocationChanges: true,
  locationChangeMaxCount: 3,
  locationChangeCooldownSec: 30,
  currency: 'EUR',
  pricePerMinute: 2,
  therapistBillPct: 0.5,
  eventBillPct: 0.5,
  therapistTipPct: 1.0,
  allowUnderpayment: true,
  requireUnderpaymentReason: false,
  bookingMinuteOptions: [10, 15, 20, 30, 45, 60, 90, 120],
  extendMinuteOptions: [10, 15, 30, 45, 60],
  defaultBookingMinutes: 30,
  askCustomerAmount: false,
  askCustomerRating: false,
} as const;

// Status colors for UI
export const STATUS_COLORS = {
  grey: '#6B7280',   // off duty
  blue: '#3B82F6',   // break
  red: '#EF4444',    // active session
  orange: '#F97316', // session ending soon
  green: '#22C55E',  // available
} as const;

// ============================================
// Settlement Models
// ============================================

export type SettlementStatus = 'pending' | 'confirmed' | 'disputed';

export interface Settlement {
  id: string;
  therapistId: string;
  therapistName: string;
  date: string; // YYYY-MM-DD

  // Session summary
  totalSessions: number;
  totalMinutes: number;

  // Financial summary
  totalAmountReceived: number;  // Cash collected by therapist
  totalBillAmount: number;      // Total bill amount for sessions
  therapistPayout: number;      // Amount therapist keeps
  eventPayout: number;          // Amount therapist owes to event
  totalTips: number;            // Tips included in therapist payout

  // Status
  status: SettlementStatus;

  // Timestamps
  createdAt: Timestamp;
  createdBy: string;            // Therapist UID who initiated
  confirmedAt?: Timestamp;
  confirmedBy?: string;         // Admin UID who confirmed

  // Notes
  therapistNotes?: string;
  adminNotes?: string;
}

// ============================================
// Landing Page Sign-ups
// ============================================

export interface LandingSignUp {
  id: string;
  name: string;
  email: string;
  phone?: string;
  organization: string;
  role: 'organizer' | 'massage_team' | 'venue' | 'other';
  message?: string;
  createdAt: Timestamp;
  status: 'new' | 'contacted' | 'converted' | 'closed';
  notes?: string;
}
